// ListingDetailModal — Marketplace 2.0 commit surface.
// Spec: Playbook § 5.2 (Decision 2) + BUILD_GUIDE § 1.9 Step 3.
// Demo: marketplace_demo.jsx ListingDetailModal (~lines 544–804).
//
// The modal container stays mounted across the detail ⇄ trust-history
// transition (Decision 5 replace-in-place). Internal `view` state swaps
// the inner content; the panel doesn't unmount.

import React, { useState, useEffect, useCallback } from 'react';
import TrustBadge from './TrustBadge';
import TrustHistoryView from './TrustHistoryView';
import VerificationGateModal from './VerificationGateModal';
import { supabase } from '../supabaseClient';

const ESCROW_THRESHOLD = 500; // $500+ → escrow gate (Playbook § 6.4)

// SPA navigation helper — same pushState/popstate pattern used elsewhere
// (Conversation.js, App.js routing). Avoids dropping a hard <a href> just to
// open a thread URL.
function navTo(path) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

// Buyer profile-completeness gate. A purchase needs a public username, a
// legal name, a full shipping address, and a contact email — without them we
// can't label a package or identify the payer, so we don't mint a payment /
// escrow link. Returns the list of missing field groups (empty = ready to buy).
// contact_email is optional in the profile row, so we fall back to the auth
// identity, which every signed-in buyer has.
async function fetchProfileGaps(userId) {
  const { data, error } = await supabase
    .from('profiles')
    .select('username, contact_email, legal_first_name, legal_last_name, shipping_address_line1, shipping_city, shipping_state, shipping_postal_code')
    .eq('id', userId)
    .single();
  if (error) throw error;
  const v = (x) => (x || '').trim();
  let email = v(data?.contact_email);
  if (!email) {
    try { const { data: u } = await supabase.auth.getUser(); email = v(u?.user?.email); } catch (_) {}
  }
  const missing = [];
  if (!v(data?.username)) missing.push('username');
  if (!email) missing.push('email');
  if (!v(data?.legal_first_name) || !v(data?.legal_last_name)) missing.push('name');
  if (!v(data?.shipping_address_line1) || !v(data?.shipping_city) ||
      !v(data?.shipping_state) || !v(data?.shipping_postal_code)) missing.push('shipping address');
  return missing;
}

export default function ListingDetailModal({ listing, onClose, showToast, currentUserId }) {
  const [view, setView] = useState('detail'); // 'detail' | 'history'
  const [historyTab, setHistoryTab] = useState('seller');

  // ESC dismisses the modal at any view
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const openHistory = useCallback((tab) => {
    setHistoryTab(tab);
    setView('history');
  }, []);
  const backToDetail = useCallback(() => setView('detail'), []);

  if (!listing) return null;

  return (
    <>
      <style>{MODAL_CSS}</style>
      <div className="mp-modal-overlay" onClick={onClose}>
        <div
          className="mp-modal-panel"
          role="dialog"
          aria-modal="true"
          aria-label={view === 'detail' ? 'Listing detail' : 'Trust history'}
          onClick={(e) => e.stopPropagation()}
        >
          {view === 'detail' ? (
            <DetailContent
              listing={listing}
              onClose={onClose}
              onOpenHistory={openHistory}
              showToast={showToast}
              currentUserId={currentUserId}
            />
          ) : (
            <TrustHistoryView
              seller={listing.seller}
              initialTab={historyTab}
              onBack={backToDetail}
              onClose={onClose}
            />
          )}
        </div>
      </div>
    </>
  );
}

// ───────────────────────────────────────────────────────────────────
// Detail content
// ───────────────────────────────────────────────────────────────────

function DetailContent({ listing, onClose, onOpenHistory, showToast, currentUserId }) {
  const {
    cardKey, set, setName, cardNumber, serial, parallel, parallelNum,
    name, grade, price, estValue,
    status, statusType,
    escrow,
    seller = {},
    imageUrl, imageBackUrl, conditionPhotos = [],
    acceptsPaypal, acceptsVenmo,
  } = listing;

  const isEscrow = escrow || (typeof price === 'number' && price >= ESCROW_THRESHOLD);
  const isTrade = statusType === 'trade';
  // The viewer is also the seller — buy/trade flows don't apply; show a
  // self-listing note instead of trying to let them transact with themselves.
  const isOwnListing = !!currentUserId && !!seller?.id && currentUserId === seller.id;

  // Phase 2E.2 — Buy with PayPal / Venmo wiring.
  // Phase 2E.3 extends this to 'escrow' (mutually exclusive with paypal/venmo
  // in render — isEscrow listings hide PayPal/Venmo entirely — but sharing
  // one state machine keeps the re-entrancy guard simple).
  const [paying, setPaying] = useState(null); // null | 'paypal' | 'venmo' | 'escrow'

  // Phase 2E.3 Step 6 — buyer-side verification gate. Opens when
  // marketplace-create-escrow-deal returns 412 verification_required.
  // On success, the gate's onVerified callback re-invokes startEscrow so
  // the buyer doesn't have to tap Open Escrow a second time.
  const [verifyGateOpen, setVerifyGateOpen] = useState(false);
  const [retryAfterVerify, setRetryAfterVerify] = useState(false);

  // Marketplace 2.1 — Sold Feed Phase 2: inline "Recent sales on CK" comps for
  // THIS exact card (same set / card# / parallel, ignoring serial). Sourced from
  // the marketplace-sold-feed edge fn in per-card mode ({ cardKey }). Read-only;
  // the buyer is never in the payload (server-enforced). This is the same UI
  // slot Gap #6 (eBay comps) will later share.
  //   comps === null  → still loading
  //   comps === { count, stats, rows } → loaded (count 0 = no CK sales yet)
  const [comps, setComps] = useState(null);
  const [compsError, setCompsError] = useState(false);
  useEffect(() => {
    if (!cardKey) { setComps({ count: 0, stats: null, rows: [] }); return; }
    let alive = true;
    setComps(null);
    setCompsError(false);
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke(
          'marketplace-sold-feed', { body: { cardKey } },
        );
        if (error) throw error;
        if (alive) setComps({ count: data?.count || 0, stats: data?.stats || null, rows: data?.rows || [] });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[sold-feed comps] fetch failed:', e?.message || e);
        if (alive) { setComps({ count: 0, stats: null, rows: [] }); setCompsError(true); }
      }
    })();
    return () => { alive = false; };
  }, [cardKey]);

  // External eBay reference (revived Gap #6). The Netlify `ebay-comps` function
  // returns two halves, both title-filtered to the SAME parallel / print run /
  // grade:
  //   active → Browse API current asking prices (always available)
  //   sold   → Marketplace Insights completed sales, last 90d. Limited Release:
  //            stays null until eBay grants the scope, so the sold column ships
  //            DORMANT and auto-activates on approval (no redeploy).
  //   ebay === null → loading; ebay === { active, sold } → loaded.
  const [ebay, setEbay] = useState(null);
  useEffect(() => {
    if (!cardKey) { setEbay({ active: { median: null, count: 0 }, sold: null }); return; }
    let alive = true;
    setEbay(null);
    // Mirrors how sellers title eBay listings, e.g.
    // "2025 Topps Chrome Galaxy #23 Bounty Hunters Prism Refractor /75 PSA 10".
    const query = [
      setName || set,
      cardNumber ? `#${cardNumber}` : null,
      name,
      parallel,
      parallelNum ? `/${parallelNum}` : null,
      grade,
    ].filter(Boolean).join(' ');
    const params = new URLSearchParams({ query, cardKey });
    if (parallel) params.set('parallel', parallel);
    if (parallelNum) params.set('printRun', String(parallelNum));
    // grade present → graded comps of the SAME company+grade; absent → raw only.
    if (grade) params.set('grade', grade);
    (async () => {
      try {
        const res = await fetch(`/.netlify/functions/ebay-comps?${params.toString()}`);
        if (!res.ok) throw new Error(`ebay-comps ${res.status}`);
        const data = await res.json();
        // Back-compat: older function returned flat active fields, no `active`.
        const active = data?.active ?? (data && 'median' in data
          ? { median: data.median ?? null, avgPrice: data.avgPrice ?? null, minPrice: data.minPrice ?? null, maxPrice: data.maxPrice ?? null, count: data.count || 0 }
          : null);
        if (alive) setEbay({ active, sold: data?.sold ?? null });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[ebay-comps] fetch failed:', e?.message || e);
        if (alive) setEbay({ active: { median: null, count: 0 }, sold: null });
      }
    })();
    return () => { alive = false; };
  }, [cardKey, setName, set, cardNumber, name, parallel, parallelNum, grade]);

  // Buyer profile-completeness gate (shared by PayPal/Venmo + escrow). If the
  // buyer hasn't set up username, name, shipping address, and email, we don't
  // start a purchase — toast what's missing and drop them on the Profile page
  // to finish setup. Fails open on a transient read error so a flaky network
  // never traps a fully-set-up buyer. Returns true when the buyer may proceed.
  // Toast the buyer what's missing and drop them on the Profile page to finish
  // setup. Shared by the pre-flight client check and the server's 412
  // profile_incomplete backstop (so a bypassed client check lands the same way).
  const sendToProfileSetup = useCallback((missing) => {
    const list = Array.isArray(missing) && missing.length ? ` (${missing.join(', ')})` : '';
    if (showToast) showToast(`Finish setting up your profile${list} before you can buy.`);
    if (onClose) onClose();
    navTo('/profile');
  }, [showToast, onClose]);

  const ensureProfileReady = useCallback(async () => {
    if (!currentUserId) return true; // auth is enforced upstream; nothing to check
    let missing;
    try {
      missing = await fetchProfileGaps(currentUserId);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[Marketplace] profile-completeness check failed:', e?.message || e);
      return true; // fail open — the edge function re-checks server-side
    }
    if (missing.length === 0) return true;
    sendToProfileSetup(missing);
    return false;
  }, [currentUserId, sendToProfileSetup]);

  const startPayment = useCallback(async (method) => {
    if (paying) return; // re-entrancy guard
    if (!cardKey || !seller?.id) {
      if (showToast) showToast('Listing data is incomplete — try reopening the modal.');
      return;
    }
    if (!(await ensureProfileReady())) return;
    setPaying(method);
    try {
      const cardSnapshot = {
        setName: setName || set || null,
        cardN: cardNumber,
        parallelName: parallel || null,
        price: typeof price === 'number' ? price : null,
        status: 'for_sale',
      };
      const { data, error } = await supabase.functions.invoke(
        'marketplace-create-payment-link',
        {
          body: {
            sellerId: seller.id,
            cardKey,
            paymentMethod: method,
            cardSnapshot,
          },
        },
      );
      if (error) {
        // supabase-js wraps non-2xx responses in a FunctionsHttpError whose
        // .message is the unhelpful "Edge Function returned a non-2xx status
        // code". The real body is on error.context (a Response). Try to read
        // the `error` field our function emits, so the toast can actually tell
        // the buyer what went wrong.
        let detail = null;
        let detailMissing = null;
        try {
          if (error.context && typeof error.context.json === 'function') {
            const body = await error.context.json();
            if (body && typeof body.error === 'string') detail = body.error;
            if (body && Array.isArray(body.missing)) detailMissing = body.missing;
          }
        } catch (_) { /* fall through to generic */ }
        // Server backstop: buyer profile not set up. Mirror the pre-flight gate
        // (toast + route to /profile) instead of surfacing a raw error.
        if (detail === 'profile_incomplete') {
          setPaying(null);
          sendToProfileSetup(detailMissing);
          return;
        }
        // June $5 promo: the server caps each buyer at one paid $5 card for
        // the month. Translate the raw code into a friendly, on-brand toast.
        if (detail === 'promo_limit_reached') {
          throw new Error("You've already grabbed your $5 promo card this month — limit is one per buyer during June. Thanks for trying Marketplace!");
        }
        throw new Error(detail || error.message || 'Payment link could not be generated.');
      }
      if (!data?.paymentLink || !data?.conversationId) {
        throw new Error('Edge function response missing paymentLink or conversationId.');
      }
      // Open the payment link in a new tab — buyer completes payment there.
      // window.open returns null if popups are blocked; we still navigate to
      // the conversation so the link is also tappable from inside the thread.
      try { window.open(data.paymentLink, '_blank', 'noopener'); } catch (_) {}
      // Close this modal and route to the conversation thread (Step 6 will
      // render the full state machine; for now it's the existing chat view).
      if (onClose) onClose();
      navTo(`/messages/${data.conversationId}`);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Marketplace 2.0] startPayment failed:', err);
      const msg = (err && err.message) || 'Payment link could not be generated.';
      if (showToast) showToast(msg);
      setPaying(null);
    }
  }, [paying, cardKey, seller, setName, set, cardNumber, parallel, price, onClose, showToast, ensureProfileReady, sendToProfileSetup]);

  // Phase 2E.3 — Open Escrow wiring. Mirrors startPayment(); diverges only
  // on (a) no method param (escrow has no variants), (b) 412 verification-
  // required handling — when the buyer hasn't completed Persona, the
  // function returns { error: 'verification_required', party: 'buyer' }
  // and the client opens BuyerVerificationGate (Step 6) instead of toasting.
  // Until Step 6 lands, the 412 surfaces as a provisional toast.
  const startEscrow = useCallback(async () => {
    if (paying) return;
    if (!cardKey || !seller?.id) {
      if (showToast) showToast('Listing data is incomplete — try reopening the modal.');
      return;
    }
    if (!(await ensureProfileReady())) return;
    setPaying('escrow');
    try {
      const cardSnapshot = {
        setName: setName || set || null,
        cardN: cardNumber,
        parallelName: parallel || null,
        price: typeof price === 'number' ? price : null,
        status: 'for_sale',
      };
      const { data, error } = await supabase.functions.invoke(
        'marketplace-start-deal',
        {
          body: {
            sellerId: seller.id,
            cardKey,
            cardSnapshot,
          },
        },
      );
      if (error) {
        // Same FunctionsHttpError unwrap pattern as startPayment — peel the
        // real `error` field off error.context (a Response) so toasts say
        // what actually went wrong.
        let detailError = null;
        let detailBody = null;
        try {
          if (error.context && typeof error.context.json === 'function') {
            detailBody = await error.context.json();
            if (detailBody && typeof detailBody.error === 'string') detailError = detailBody.error;
          }
        } catch (_) { /* fall through to generic */ }
        // Server backstop: buyer profile not set up. Mirror the pre-flight gate
        // (toast + route to /profile) instead of surfacing a raw error.
        if (detailError === 'profile_incomplete') {
          setPaying(null);
          sendToProfileSetup(Array.isArray(detailBody?.missing) ? detailBody.missing : null);
          return;
        }
        // 412 verification_required → buyer hasn't verified. Open the gate
        // (VerificationGateModal in mode='buyer'). On success the gate's
        // onVerified handler re-invokes startEscrow so the buyer doesn't
        // have to tap Open Escrow a second time.
        if (detailError === 'verification_required') {
          setPaying(null);
          setRetryAfterVerify(true);
          setVerifyGateOpen(true);
          return;
        }
        throw new Error(detailError || error.message || 'Escrow link could not be generated.');
      }
      if (!data?.conversationId) {
        throw new Error('Edge function response missing conversationId.');
      }
      // Marketplace 2.1: a >=$500 deal opens in the thread's escrow-recommended
      // routing card — no payment link is minted yet. The buyer funds escrow
      // (or the parties mutually opt out to PayPal G&S) from inside the thread.
      if (onClose) onClose();
      navTo(`/messages/${data.conversationId}`);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Marketplace 2.0] startEscrow failed:', err);
      const msg = (err && err.message) || 'Escrow link could not be generated.';
      if (showToast) showToast(msg);
      setPaying(null);
    }
  }, [paying, cardKey, seller, setName, set, cardNumber, parallel, price, onClose, showToast, ensureProfileReady, sendToProfileSetup]);

  // Tertiary / escape-hatch CTAs (Trade, "Message seller first") stay stubbed
  // until their own phases — trade is its own flow, Message-first is
  // post-launch v1.5.
  const handleStubCta = (method) => {
    // eslint-disable-next-line no-console
    console.log('[Marketplace 2.0] CTA stub:', method, listing);
    if (showToast) showToast(`Coming soon — ${method} wiring is out of Phase 2E.3 scope.`);
  };

  return (
    <>
      {/* Sticky header */}
      <div className="mp-modal-header">
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontSize: 9,
            color: 'var(--muted-5)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontFamily: "'Share Tech Mono', monospace",
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>{set}</div>
          <div style={{
            fontSize: 15,
            fontWeight: 500,
            color: 'var(--text-bright)',
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            Card #{cardNumber}{serial ? ` · Serial #${serial}` : ''}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            background: 'transparent',
            border: 'none',
            fontSize: 22,
            color: 'var(--muted-5)',
            cursor: 'pointer',
            padding: 4,
            lineHeight: 1,
            minWidth: 44,
            minHeight: 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >×</button>
      </div>

      <div className="mp-modal-body">
        {/* Parallel tag */}
        {parallel && (
          <div style={{
            display: 'inline-block',
            fontSize: 10,
            padding: '3px 8px',
            border: '0.5px solid var(--sketch-orange)',
            color: 'var(--peach)',
            borderRadius: 3,
            marginBottom: 14,
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            {parallel}{parallelNum ? ` /${parallelNum}` : ''}
          </div>
        )}

        {/* Escrow banner */}
        {isEscrow && (
          <div style={{
            background: 'rgba(var(--amber-rgb),0.1)',
            border: '0.5px solid rgba(var(--amber-rgb),0.4)',
            borderRadius: 4,
            padding: '10px 12px',
            marginBottom: 14,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <span style={{ fontSize: 14 }}>🛡</span>
              <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--warn)', fontFamily: "'JetBrains Mono', monospace" }}>
                Escrow recommended
              </span>
            </div>
            <div style={{ fontSize: 10, color: 'var(--muted-5)', lineHeight: 1.5 }}>
              Recommended for $500+ cards — Escrow.com holds payment until delivery is confirmed. Optional if both buyer and seller agree to skip it.
            </div>
          </div>
        )}

        {/* Photos — hero + thumbnail strip; only slots that actually exist are shown */}
        <ListingGallery
          name={name}
          candidates={[
            { slot: 'front', label: 'Front', url: imageUrl },
            { slot: 'back',  label: 'Back',  url: imageBackUrl },
            ...conditionPhotos,
          ]}
        />

        {/* Details block */}
        <div style={{
          background: 'rgba(var(--wash-rgb),0.03)',
          borderRadius: 4,
          padding: '10px 12px',
          marginBottom: 14,
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: 8,
          }}>
            <span style={{ fontSize: 11, color: 'var(--muted-5)' }}>Price</span>
            <span style={{
              fontSize: 22,
              fontWeight: 500,
              color: 'var(--warn)',
              fontFamily: "'JetBrains Mono', monospace",
              lineHeight: 1,
            }}>{price ? `$${price}` : 'Trade only'}</span>
          </div>
          {grade && (
            <DetailRow label="Grade" value={grade} mono />
          )}
          {estValue != null && (
            <DetailRow label="Est. value" value={`$${estValue}`} mono />
          )}
          <DetailRow
            label="Status"
            value={status || (isTrade ? 'FOR TRADE' : 'FOR SALE')}
            valueColor={isTrade ? 'var(--teal)' : 'var(--violet)'}
          />
          {isEscrow && (
            <DetailRow label="Escrow fee" value="~$38 (split)" />
          )}
        </div>

        {/* Recent sales on CK (Marketplace 2.1 — Sold Feed Phase 2 / internal comps) */}
        <RecentSalesStrip comps={comps} error={compsError} />

        {/* eBay reference: Active asking + Sold 90d (Gap #6). Sold ships
            dormant until eBay Marketplace Insights access is granted. */}
        <EbayReferenceStrip ebay={ebay} gradeLabel={grade || 'raw'} />

        {/* Seller block */}
        <div style={{
          borderTop: '0.5px solid rgba(var(--wash-rgb),0.06)',
          paddingTop: 12,
          marginBottom: 14,
        }}>
          <div style={{
            fontSize: 10,
            color: 'var(--muted-5)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: 8,
            fontFamily: "'Share Tech Mono', monospace",
          }}>Seller</div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            marginBottom: 8,
          }}>
            <span style={{
              fontSize: 13,
              color: 'var(--violet)',
              fontWeight: 500,
              fontFamily: "'JetBrains Mono', monospace",
            }}>{seller.handle}</span>
            {seller.verified && (
              <span style={{
                fontSize: 9,
                padding: '2px 6px',
                background: 'rgba(var(--ok-2-rgb),0.15)',
                color: 'var(--ok)',
                borderRadius: 2,
                border: '0.5px solid rgba(var(--ok-2-rgb),0.4)',
                fontWeight: 500,
                letterSpacing: '0.04em',
                fontFamily: "'JetBrains Mono', monospace",
              }}>✓ VERIFIED</span>
            )}
          </div>

          <div style={{ display: 'flex', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
            {seller.sellerColor && (
              <TrustBadge
                color={seller.sellerColor}
                count={seller.sellerCount}
                role="seller"
                size="md"
                interactive={true}
                onClick={() => onOpenHistory('seller')}
              />
            )}
            {seller.buyerColor && (
              <TrustBadge
                color={seller.buyerColor}
                count={seller.buyerCount}
                role="buyer"
                size="md"
                interactive={true}
                onClick={() => onOpenHistory('buyer')}
              />
            )}
          </div>
          <div style={{ fontSize: 10, color: '#5a6580' }}>Tap a badge to see full transaction history</div>
        </div>

        {/* Shipping microcopy */}
        <div style={{
          fontSize: 10,
          color: 'var(--muted-5)',
          padding: '8px 10px',
          background: 'rgba(55, 138, 221, 0.08)',
          borderRadius: 3,
          marginBottom: 14,
          lineHeight: 1.4,
        }}>
          <span style={{ color: 'var(--sky)' }}>ⓘ</span>{' '}
          {isEscrow
            ? 'Tracked + insured shipping required for escrow listings.'
            : 'Tracked shipping required. 48 hours to confirm receipt after delivery.'}
        </div>

        {/* CTAs — Decision 2 hierarchy */}
        <div className="mp-modal-ctas">
          {isOwnListing ? (
            // You can't transact with yourself. Replace the Buy/Trade CTAs with
            // a quiet note + the path back to your own listing controls.
            <div style={{
              fontSize: 11,
              color: 'var(--muted-5)',
              textAlign: 'center',
              padding: '12px 10px',
              lineHeight: 1.5,
              background: 'rgba(var(--violet-rgb),0.04)',
              border: '0.5px solid rgba(var(--violet-rgb),0.18)',
              borderRadius: 4,
            }}>
              This is your own listing. Edit price, photos, or status from your collection — buyers will see the changes here.
            </div>
          ) : isEscrow ? (
            <>
              <button
                onClick={startEscrow}
                disabled={!!paying}
                style={{ ...ctaStyle('var(--teal)', 'white'), opacity: paying && paying !== 'escrow' ? 0.5 : 1 }}
              >
                {paying === 'escrow'
                  ? 'Starting…'
                  : `Continue${price ? ` · $${price}` : ''}`}
              </button>
              <button onClick={() => handleStubCta('messaging')} disabled={!!paying} style={ctaSecondaryStyle()}>
                Message seller first
              </button>
            </>
          ) : isTrade ? (
            <>
              <button onClick={() => handleStubCta('trade')} style={ctaStyle('var(--teal)', 'var(--chip-teal)')}>
                Propose a trade
              </button>
              <button onClick={() => handleStubCta('messaging')} style={ctaSecondaryStyle()}>
                Message owner first
              </button>
            </>
          ) : (acceptsPaypal || acceptsVenmo) ? (
            <>
              {acceptsPaypal && (
                <button
                  onClick={() => startPayment('paypal')}
                  disabled={!!paying}
                  style={{ ...ctaStyle('#003087', 'white'), opacity: paying && paying !== 'paypal' ? 0.5 : 1 }}
                >
                  {paying === 'paypal'
                    ? 'Generating link…'
                    : `Buy with PayPal${price ? ` · $${price}` : ''}`}
                </button>
              )}
              {acceptsVenmo && (
                <button
                  onClick={() => startPayment('venmo')}
                  disabled={!!paying}
                  style={{ ...ctaStyle('#3D95CE', 'white'), opacity: paying && paying !== 'venmo' ? 0.5 : 1 }}
                >
                  {paying === 'venmo'
                    ? 'Generating link…'
                    : `Buy with Venmo${price ? ` · $${price}` : ''}`}
                </button>
              )}
              <button onClick={() => handleStubCta('messaging')} disabled={!!paying} style={ctaSecondaryStyle()}>
                Message seller first
              </button>
            </>
          ) : (
            // Defensive fallback — Step 5's For-Sale gate prevents this at save time,
            // but if a row slipped through (e.g. seller toggled payment methods off
            // after publishing), the modal still has a graceful path.
            <>
              <button onClick={() => handleStubCta('messaging')} style={ctaStyle('var(--violet)', 'var(--chip-indigo)')}>
                Message seller
              </button>
              <div style={{
                fontSize: 10,
                color: 'var(--muted-5)',
                textAlign: 'center',
                padding: '6px 0',
                lineHeight: 1.5,
              }}>This seller hasn't set up payment methods yet.</div>
            </>
          )}
        </div>

        {/* CK-never-touches-money footnote — the most important sentence in the modal */}
        <div style={{
          fontSize: 9,
          color: '#5a6580',
          textAlign: 'center',
          marginTop: 12,
          lineHeight: 1.5,
        }}>
          Payment goes directly to {seller.handle || 'the seller'}.<br />
          CollectionKeeper never touches your money.
        </div>
      </div>

      {/* Phase 2E.3 Step 6 — buyer-side verification gate (Decision 6).
          Renders above this modal (z-index 2100 vs 2000) when an unverified
          buyer hits the 412 verification_required path on Open Escrow. */}
      {verifyGateOpen && (
        <VerificationGateModal
          user={{ id: currentUserId }}
          mode="buyer"
          priceUsd={typeof price === 'number' ? price : null}
          onClose={() => {
            setVerifyGateOpen(false);
            setRetryAfterVerify(false);
          }}
          onVerified={() => {
            setVerifyGateOpen(false);
            if (retryAfterVerify) {
              setRetryAfterVerify(false);
              // Defer one tick so React unmounts the gate before the second
              // attempt fires — keeps the loading spinner state clean.
              setTimeout(() => { startEscrow(); }, 50);
            }
          }}
        />
      )}
    </>
  );
}

// ───────────────────────────────────────────────────────────────────
// Helpers
// ───────────────────────────────────────────────────────────────────

// Marketplace 2.1 — Sold Feed Phase 2. CK's own completed-sale comps for this
// exact card (any serial). Read-only price history; the slot Gap #6 (eBay
// comps) will later share. `comps` is null while loading.
function RecentSalesStrip({ comps, error }) {
  const wrap = {
    background: 'rgba(var(--ok-rgb),0.04)',
    border: '0.5px solid rgba(var(--ok-rgb),0.18)',
    borderRadius: 4,
    padding: '10px 12px',
    marginBottom: 14,
  };
  const header = {
    fontSize: 10,
    color: 'var(--muted-5)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    fontFamily: "'Share Tech Mono', monospace",
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: 8,
  };

  if (comps == null) {
    return (
      <div style={wrap}>
        <div style={header}><span>Recent sales on CK</span></div>
        <div style={{ fontSize: 10, color: '#5a6580' }}>Loading…</div>
      </div>
    );
  }

  if (error || comps.count === 0) {
    return (
      <div style={wrap}>
        <div style={header}><span>Recent sales on CK</span></div>
        <div style={{ fontSize: 10, color: '#5a6580', lineHeight: 1.4 }}>
          {error
            ? 'Sales history is unavailable right now.'
            : 'No recorded CK sales for this card yet.'}
        </div>
      </div>
    );
  }

  const { count, stats, rows } = comps;
  const range = stats && stats.low != null
    ? (stats.low === stats.high ? `$${stats.low}` : `$${stats.low}–$${stats.high}`)
    : '—';

  return (
    <div style={wrap}>
      <div style={header}>
        <span>Recent sales on CK</span>
        <span style={{ color: '#5a6580' }}>n={count}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <span style={{ fontSize: 11, color: 'var(--muted-5)' }}>Range</span>
        <span style={{
          fontSize: 16,
          fontWeight: 500,
          color: 'var(--ok)',
          fontFamily: "'JetBrains Mono', monospace",
          lineHeight: 1,
        }}>{range}</span>
      </div>
      {stats && stats.median != null && (
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '2px 0', marginBottom: 6 }}>
          <span style={{ color: 'var(--muted-5)' }}>Median</span>
          <span style={{ color: 'var(--text-bright)', fontFamily: "'JetBrains Mono', monospace" }}>${stats.median}</span>
        </div>
      )}
      {(rows || []).slice(0, 5).map((r) => (
        <div key={r.id} style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 10,
          color: 'var(--muted-5)',
          padding: '2px 0',
        }}>
          <span>{r.soldDate}{r.seller ? ` · ${r.seller}` : ''}</span>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", color: 'var(--violet)' }}>
            {r.price != null ? `$${r.price}` : '—'}
          </span>
        </div>
      ))}
      <div style={{ fontSize: 9, color: '#5a6580', marginTop: 6, lineHeight: 1.4 }}>
        Completed CK sales of this card (any serial). Internal data — not a market quote.
      </div>
    </div>
  );
}

// External eBay reference — two columns: ACTIVE asking (Browse API, current
// listings) and SOLD 90d (Marketplace Insights, completed sales). The sold
// column ships DORMANT (dashed "SOON" placeholder) until eBay grants the
// Insights scope, then auto-activates. Anchored on the active median: if eBay
// has no matching active listing, the whole strip stays hidden (as before).
function EbayReferenceStrip({ ebay, gradeLabel }) {
  const active = (ebay && ebay.active) || null;
  const sold = ebay ? ebay.sold : null; // null/undefined = scope not granted (dormant)
  const med = (s) => (s ? (s.median != null ? s.median : s.avgPrice) : null);
  const aMed = med(active);
  const sAvailable = sold != null; // object means Insights responded
  const sMed = med(sold);
  if (aMed == null && sMed == null) return null;

  const fmt = (n) => `$${Math.round(Number(n))}`;
  const rangeOf = (s) => (s && s.minPrice != null && s.maxPrice != null && s.minPrice !== s.maxPrice)
    ? `${fmt(s.minPrice)}–${fmt(s.maxPrice)}` : null;
  const delta = (aMed && sMed) ? Math.round(((sMed - aMed) / aMed) * 100) : null;

  const pill = (text, on) => (
    <span style={{
      fontSize: 8,
      letterSpacing: '0.06em',
      padding: '2px 5px',
      borderRadius: 3,
      marginLeft: 5,
      fontFamily: "'Share Tech Mono', monospace",
      background: on ? 'rgba(var(--green-rgb),0.14)' : 'rgba(var(--wash-rgb),0.05)',
      color: on ? 'var(--green)' : '#5a6580',
    }}>{text}</span>
  );
  const col = { flex: 1, minWidth: 0, borderRadius: 5, padding: '9px 10px', background: 'rgba(var(--wash-rgb),0.02)', border: '0.5px solid rgba(var(--wash-rgb),0.06)' };
  const ctitle = { fontFamily: "'Share Tech Mono', monospace", fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 7, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' };
  const big = { fontFamily: "'JetBrains Mono', monospace", fontWeight: 500, fontSize: 19, lineHeight: 1 };
  const rngS = { fontSize: 10, color: 'var(--muted-5)', marginTop: 5, fontFamily: "'JetBrains Mono', monospace" };
  const subS = { fontSize: 9, color: '#5a6580', marginTop: 2 };

  return (
    <div style={{ background: 'rgba(var(--green-rgb),0.04)', border: '0.5px solid rgba(var(--green-rgb),0.18)', borderRadius: 6, padding: '11px 12px', marginBottom: 14 }}>
      <div style={{ fontSize: 10, color: 'var(--muted-5)', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: "'Share Tech Mono', monospace", display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
        <span>eBay reference{gradeLabel ? ` · ${gradeLabel}` : ''}</span>
        <span style={{ color: '#5a6580' }}>
          {active && active.count ? `active ${active.count}` : ''}
          {sAvailable && sold.count ? ` · sold ${sold.count}` : ''}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        {/* Active asking */}
        <div style={col}>
          <div style={{ ...ctitle, color: 'var(--card-title-gold)' }}>
            <span>Active asking</span>
            {active && active.count ? <span style={{ color: '#5a6580', letterSpacing: 0 }}>n={active.count}</span> : null}
          </div>
          <div style={{ ...big, color: aMed != null ? 'var(--warn)' : '#5a6580' }}>{aMed != null ? fmt(aMed) : '—'}</div>
          {rangeOf(active) && <div style={rngS}>{rangeOf(active)}</div>}
          <div style={subS}>{aMed != null ? 'median, current BIN' : 'no active listings'}</div>
        </div>

        {/* Sold 90d — live, empty, or dormant */}
        {sAvailable ? (
          <div style={col}>
            <div style={{ ...ctitle, color: 'var(--green)' }}>
              <span>Sold · 90d{pill('LIVE', true)}</span>
              {sold.count ? <span style={{ color: '#5a6580', letterSpacing: 0 }}>n={sold.count}</span> : null}
            </div>
            <div style={{ ...big, color: sMed != null ? 'var(--green)' : '#5a6580' }}>{sMed != null ? fmt(sMed) : '—'}</div>
            {rangeOf(sold) && <div style={rngS}>{rangeOf(sold)}</div>}
            <div style={subS}>{sMed != null ? 'median, completed' : 'no recent sales'}</div>
          </div>
        ) : (
          <div style={{
            ...col,
            border: '0.5px dashed rgba(var(--wash-rgb),0.10)',
            background: 'repeating-linear-gradient(45deg, rgba(var(--wash-rgb),0.015), rgba(var(--wash-rgb),0.015) 6px, transparent 6px, transparent 12px)',
          }}>
            <div style={{ ...ctitle, color: '#5a6580' }}><span>Sold · 90d{pill('SOON', false)}</span></div>
            <div style={{ ...big, color: '#5a6580', fontSize: 15 }}>—</div>
            <div style={{ fontSize: 9, color: '#5a6580', marginTop: 6, lineHeight: 1.4 }}>Completed-sale comps unlock when eBay grants access.</div>
          </div>
        )}
      </div>

      {delta != null && (
        <div style={{ fontSize: 9, color: 'var(--muted-5)', marginTop: 8, fontFamily: "'JetBrains Mono', monospace" }}>
          Sold runs <b style={{ color: 'var(--green)', fontWeight: 500 }}>~{Math.abs(delta)}% {delta < 0 ? 'below' : delta > 0 ? 'above' : '='} asking</b> — typical ask-vs-sold gap.
        </div>
      )}
      <div style={{ fontSize: 9, color: '#5a6580', marginTop: 6, lineHeight: 1.45 }}>
        Matched to this parallel · print run · grade ({gradeLabel}). Active = current eBay listings (asking).{' '}
        {sAvailable ? 'Sold = completed eBay sales, last 90 days.' : 'Sold comps pending eBay Marketplace Insights access.'}
      </div>
    </div>
  );
}

function DetailRow({ label, value, mono, valueColor }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '3px 0' }}>
      <span style={{ color: 'var(--muted-5)' }}>{label}</span>
      <span style={{
        color: valueColor || 'var(--text-bright)',
        fontFamily: mono ? "'JetBrains Mono', monospace" : undefined,
      }}>{value}</span>
    </div>
  );
}

// Probes a set of candidate photo URLs and renders the ones that load as a
// hero image + thumbnail strip. Tapping the hero opens a full-screen Lightbox.
// Mirrors the probe pattern in CardImage.js (new Image() onload/onerror).
function ListingGallery({ candidates, name }) {
  const [available, setAvailable] = useState([]); // [{slot,label,url}]
  const [activeIdx, setActiveIdx] = useState(0);
  const [zoomOpen, setZoomOpen] = useState(false);

  const urlKey = candidates.map((c) => c.url).join('|');
  useEffect(() => {
    let cancelled = false;
    const probe = (c) => new Promise((resolve) => {
      if (!c.url) return resolve(null);
      const img = new Image();
      img.onload = () => resolve(c);
      img.onerror = () => resolve(null);
      img.src = c.url;
    });
    Promise.all(candidates.map(probe)).then((res) => {
      if (cancelled) return;
      setAvailable(res.filter(Boolean));
      setActiveIdx(0);
    });
    return () => { cancelled = true; };
  }, [urlKey]);

  if (available.length === 0) {
    // Preserve the old empty-state look (two NO-PHOTO cells).
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
        <ModalPhoto url={null} sideLabel="FRONT" name={name} />
        <ModalPhoto url={null} sideLabel="BACK" name={name} />
      </div>
    );
  }

  const active = available[activeIdx] || available[0];

  return (
    <div style={{ marginBottom: 16 }}>
      {/* hero */}
      <div
        onClick={() => setZoomOpen(true)}
        style={{ position: 'relative', aspectRatio: '3 / 4', maxHeight: 360, margin: '0 auto 8px', borderRadius: 6, overflow: 'hidden', background: 'var(--bg-2)', border: '0.5px solid rgba(var(--wash-rgb),0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-in' }}
      >
        <img src={active.url} alt={`${active.label} of ${name || 'card'}`} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        <div style={{ position: 'absolute', top: 8, left: 8, fontFamily: "'Share Tech Mono', monospace", fontSize: 9, letterSpacing: 1, color: 'var(--silver)', background: 'rgba(0,0,0,0.6)', padding: '3px 7px', borderRadius: 3 }}>{active.label.toUpperCase()}</div>
        {available.length > 1 && (
          <div style={{ position: 'absolute', top: 8, right: 8, fontFamily: "'Share Tech Mono', monospace", fontSize: 9, color: 'var(--silver)', background: 'rgba(0,0,0,0.6)', padding: '3px 7px', borderRadius: 3 }}>📷 {available.length}</div>
        )}
        <div style={{ position: 'absolute', bottom: 8, right: 8, fontFamily: "'Share Tech Mono', monospace", fontSize: 9, color: 'var(--sky)', background: 'rgba(0,0,0,0.55)', padding: '3px 7px', borderRadius: 3 }}>⤢ tap to zoom</div>
      </div>

      {/* thumbnail strip (only when >1 photo) — horizontally scrollable */}
      {available.length > 1 && (
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, WebkitOverflowScrolling: 'touch' }}>
          {available.map((c, i) => (
            <div key={c.slot} onClick={() => setActiveIdx(i)}
              style={{ flex: '0 0 auto', width: 64, aspectRatio: '1 / 1', borderRadius: 4, overflow: 'hidden', background: 'var(--bg-2)', border: i === activeIdx ? '1.5px solid var(--violet)' : '0.5px solid rgba(var(--wash-rgb),0.08)', cursor: 'pointer', position: 'relative' }}>
              <img src={c.url} alt={c.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, fontFamily: "'Share Tech Mono', monospace", fontSize: 7, letterSpacing: 0.4, textAlign: 'center', color: 'var(--silver)', background: 'rgba(0,0,0,0.6)', padding: '2px 0' }}>{c.label.toUpperCase()}</div>
            </div>
          ))}
        </div>
      )}

      {zoomOpen && (
        <Lightbox photos={available} startIdx={activeIdx} name={name} onClose={() => setZoomOpen(false)} />
      )}
    </div>
  );
}

// Full-screen zoom. Reuses the existing enlarged-viewer aesthetic
// (rgba(0,0,0,0.92), tap backdrop / ✕ to close) and adds ◀ ▶ paging + a rail.
function Lightbox({ photos, startIdx, name, onClose }) {
  const [idx, setIdx] = useState(startIdx || 0);
  const prev = (e) => { if (e) e.stopPropagation(); setIdx((i) => (i - 1 + photos.length) % photos.length); };
  const next = (e) => { if (e) e.stopPropagation(); setIdx((i) => (i + 1) % photos.length); };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [photos.length]);

  const active = photos[idx];

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 3000, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', paddingTop: 'calc(14px + max(env(safe-area-inset-top, 0px), var(--ck-android-inset-top, 0px)))' }}>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: 'var(--text-bright)', letterSpacing: 1 }}>
          {active.label.toUpperCase()} <span style={{ color: 'var(--muted-5)', fontSize: 11, marginLeft: 8 }}>{idx + 1} / {photos.length}</span>
        </div>
        <button onClick={(e) => { e.stopPropagation(); onClose(); }} aria-label="Close"
          style={{ background: 'rgba(var(--wash-rgb),0.08)', border: '0.5px solid rgba(var(--wash-rgb),0.2)', color: 'var(--text-bright)', width: 34, height: 34, borderRadius: 17, fontSize: 16, cursor: 'pointer' }}>✕</button>
      </div>

      <div onClick={(e) => e.stopPropagation()} style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 8px' }}>
        {photos.length > 1 && (
          <button onClick={prev} aria-label="Previous" style={arrowStyle('left')}>‹</button>
        )}
        <img src={active.url} alt={`${active.label} of ${name || 'card'}`} style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 6, border: '0.5px solid rgba(var(--wash-rgb),0.12)' }} />
        {photos.length > 1 && (
          <button onClick={next} aria-label="Next" style={arrowStyle('right')}>›</button>
        )}
      </div>

      {photos.length > 1 && (
        <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: 8, justifyContent: photos.length > 6 ? 'flex-start' : 'center', overflowX: 'auto', padding: '14px 16px max(20px, env(safe-area-inset-bottom))', WebkitOverflowScrolling: 'touch' }}>
          {photos.map((c, i) => (
            <div key={c.slot} onClick={() => setIdx(i)}
              style={{ flex: '0 0 auto', width: 56, aspectRatio: '1 / 1', borderRadius: 4, overflow: 'hidden', background: 'var(--bg-2)', border: i === idx ? '1.5px solid var(--violet)' : '0.5px solid rgba(var(--wash-rgb),0.12)', opacity: i === idx ? 1 : 0.55, cursor: 'pointer' }}>
              <img src={c.url} alt={c.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function arrowStyle(side) {
  return {
    position: 'absolute', top: '50%', transform: 'translateY(-50%)', [side]: 10,
    width: 40, height: 40, borderRadius: 20, background: 'rgba(0,0,0,0.5)',
    border: '0.5px solid rgba(var(--wash-rgb),0.2)', color: 'var(--text-bright)', fontSize: 18, cursor: 'pointer',
  };
}

function ModalPhoto({ url, sideLabel, name }) {
  return (
    <div style={{
      aspectRatio: '3 / 4',
      borderRadius: 4,
      overflow: 'hidden',
      background: 'var(--bg-2)',
      border: '0.5px solid rgba(var(--wash-rgb),0.06)',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {url ? (
          <img
            src={url}
            alt={`${sideLabel.toLowerCase()} of ${name || 'card'}`}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        ) : (
          <span style={{
            fontFamily: "'Share Tech Mono', monospace",
            fontSize: 9,
            color: 'var(--border-2)',
            letterSpacing: '2px',
          }}>NO {sideLabel}</span>
        )}
      </div>
    </div>
  );
}

function ctaStyle(bg, color) {
  return {
    background: bg,
    color,
    border: 'none',
    padding: '12px',
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: "'JetBrains Mono', monospace",
    letterSpacing: '0.02em',
    minHeight: 44,
    width: '100%',
  };
}

function ctaSecondaryStyle() {
  return {
    background: 'transparent',
    color: 'var(--violet)',
    border: '0.5px solid rgba(var(--violet-rgb),0.4)',
    padding: '10px',
    borderRadius: 4,
    fontSize: 12,
    cursor: 'pointer',
    fontFamily: "'JetBrains Mono', monospace",
    minHeight: 44,
    width: '100%',
  };
}

// Modal overlay + panel CSS, including mobile full-screen takeover.
const MODAL_CSS = `
  .mp-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
    padding: 20px;
    animation: mp-modal-fade 0.18s ease-out;
  }
  .mp-modal-panel {
    background: var(--panel-2);
    border: 0.5px solid rgba(var(--wash-rgb),0.1);
    border-radius: 12px;
    max-width: 480px;
    width: 100%;
    max-height: 90vh;
    overflow: auto;
    position: relative;
    animation: mp-modal-slide 0.22s ease-out;
    color: var(--text-bright);
    font-family: 'Rajdhani', sans-serif;
  }
  .mp-modal-header {
    position: sticky;
    top: 0;
    background: var(--panel-2);
    padding: 14px 18px;
    border-bottom: 0.5px solid rgba(var(--wash-rgb),0.06);
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    z-index: 2;
  }
  .mp-modal-body {
    padding: 18px;
  }
  .mp-modal-ctas {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  @keyframes mp-modal-fade {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes mp-modal-slide {
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @media (max-width: 600px) {
    .mp-modal-overlay {
      padding: 0;
      align-items: stretch;
    }
    .mp-modal-panel {
      max-width: 100%;
      max-height: 100vh;
      height: 100vh;
      border-radius: 0;
      border: none;
    }
    /* Full-screen takeover: the sticky header hits the physical top of the
       screen, so it must carry the native status-bar inset itself. */
    .mp-modal-header {
      padding-top: calc(14px + max(env(safe-area-inset-top, 0px), var(--ck-android-inset-top, 0px)));
    }
    .mp-modal-ctas {
      position: sticky;
      bottom: 0;
      background: var(--panel-2);
      padding: 12px 0 max(12px, env(safe-area-inset-bottom));
      margin-bottom: 0;
      border-top: 0.5px solid rgba(var(--wash-rgb),0.06);
      z-index: 2;
    }
  }
`;
